/* ═══════════════════════════════════════════════════════
   SwipeHome — Full-screen Photo Gallery Viewer (Feature 2)

   A lightweight, zero-library, vanilla-TS gallery overlay that
   opens over the listing detail page. Intentionally isolated in
   its own module so it can be reused elsewhere later without
   dragging in the listings.ts bundle.

   Behavior:
   - One-finger swipe left/right → navigate between photos
   - One-finger swipe down → close
   - Two-finger pinch → native browser zoom (via touch-action)
   - Tap outside image / tap close button / Escape / back button → close
   - Arrow keys → navigate
   - Counter in top-left updates instantly on navigate
   - Photos loaded on-demand: current photo is rendered into the
     DOM; prev/next are pre-fetched via new Image() so the swap
     is instant. Non-current photos are NOT kept in the DOM.
   - Body scroll is locked while the gallery is open.
   - history.pushState() on open so Android back / desktop back
     closes the gallery instead of navigating away from the app.

   Scope discipline:
   - Does NOT modify the existing inline carousel in listings.ts.
     The inline gallery still handles prev/next in-place; this
     overlay is an additional fullscreen mode that opens on tap.
   - Does NOT touch the swipe feed card stack. The swipe card
     has its own tap-to-advance that opens the listing detail —
     this module only runs from the detail page.
   ═══════════════════════════════════════════════════════ */

import { state } from './state.js';
import { escapeHtml, getAllMedia, imgFallback } from './ui.js';

// Void unused imports so tsc stops warning — they're referenced
// inside inline onclick / onerror strings that tsc can't see.
void imgFallback;

interface GalleryPhoto {
  url: string;
}

interface GalleryState {
  photos: GalleryPhoto[];
  currentIdx: number;
  isOpen: boolean;
  historyPushed: boolean;
}

const galleryState: GalleryState = {
  photos: [],
  currentIdx: 0,
  isOpen: false,
  historyPushed: false,
};

// ─── Entry points ──────────────────────────────────────

/**
 * Open the fullscreen gallery for the currently-selected listing.
 * Reads `state.selectedListing` and extracts all photos via the
 * same `getAllMedia()` helper the inline gallery uses.
 *
 * Called from the listing detail hero image via an inline onclick
 * handler, passing the current `data-index` so the fullscreen
 * gallery opens at the same photo the user was looking at inline.
 */
export function openPhotoGalleryForListing(startIdx: number = 0): void {
  const listing = state.selectedListing;
  if (!listing) return;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const media = getAllMedia(listing as any) as { url: string }[];
  if (!media.length) return;
  openPhotoGallery(
    media.map((m) => ({ url: m.url })),
    startIdx,
  );
}

/**
 * Low-level entry point — open the gallery with an arbitrary
 * photo list. Exposed for potential future callers (e.g. agent
 * dashboards that want to browse a listing's photos without
 * navigating to the detail page).
 */
export function openPhotoGallery(photos: GalleryPhoto[], startIdx: number = 0): void {
  if (galleryState.isOpen) return;
  if (!photos.length) return;

  galleryState.photos = photos;
  galleryState.currentIdx = Math.max(0, Math.min(startIdx, photos.length - 1));
  galleryState.isOpen = true;

  // Lock body scroll so the listing detail underneath doesn't
  // rubber-band when the user pinch-zooms the photo.
  document.body.style.overflow = 'hidden';

  // Push a history entry so the Android back gesture / desktop
  // back button closes the gallery instead of leaving the app.
  // On popstate, we close (and mark historyPushed=false so the
  // handler doesn't try to pop again).
  try {
    history.pushState({ swipehomePhotoGallery: true }, '');
    galleryState.historyPushed = true;
  } catch {
    // Some embedded webviews / iframes block pushState — fall
    // back gracefully, the close button + swipe-down still work.
    galleryState.historyPushed = false;
  }

  window.addEventListener('popstate', handleBackButton);
  window.addEventListener('keydown', handleKeydown);

  renderGallery();
  bindTouchHandlers();
  preloadAdjacent();
}

// ─── Close / teardown ──────────────────────────────────

export function closePhotoGallery(): void {
  if (!galleryState.isOpen) return;

  const overlay = document.getElementById('photo-gallery-overlay');
  overlay?.remove();

  document.body.style.overflow = '';

  // If we pushed a history entry on open, pop it now so the back
  // button state stays aligned. We clear historyPushed BEFORE
  // calling history.back() so the popstate handler sees us as
  // already-closed and doesn't loop.
  if (galleryState.historyPushed) {
    galleryState.historyPushed = false;
    try {
      history.back();
    } catch {
      /* ignore */
    }
  }

  window.removeEventListener('popstate', handleBackButton);
  window.removeEventListener('keydown', handleKeydown);

  galleryState.isOpen = false;
  galleryState.photos = [];
  galleryState.currentIdx = 0;
}

function handleBackButton(): void {
  if (!galleryState.isOpen) return;
  // popstate fired because the user hit back — our history entry
  // is already gone, so don't try to pop again.
  galleryState.historyPushed = false;
  closePhotoGallery();
}

function handleKeydown(e: KeyboardEvent): void {
  if (!galleryState.isOpen) return;
  if (e.key === 'Escape') {
    e.preventDefault();
    closePhotoGallery();
  } else if (e.key === 'ArrowLeft') {
    e.preventDefault();
    pgPrev();
  } else if (e.key === 'ArrowRight') {
    e.preventDefault();
    pgNext();
  }
}

// ─── Navigation ────────────────────────────────────────

export function pgNext(): void {
  if (!galleryState.isOpen) return;
  if (galleryState.currentIdx < galleryState.photos.length - 1) {
    galleryState.currentIdx++;
    updateCurrentSlide();
    preloadAdjacent();
  }
}

export function pgPrev(): void {
  if (!galleryState.isOpen) return;
  if (galleryState.currentIdx > 0) {
    galleryState.currentIdx--;
    updateCurrentSlide();
    preloadAdjacent();
  }
}

function updateCurrentSlide(): void {
  const counter = document.getElementById('pg-counter');
  if (counter) {
    counter.textContent = `${galleryState.currentIdx + 1} of ${galleryState.photos.length}`;
  }

  const img = document.getElementById('pg-current-img') as HTMLImageElement | null;
  if (img) {
    img.classList.remove('loaded');
    img.src = galleryState.photos[galleryState.currentIdx].url;
    img.alt = `Home photo ${galleryState.currentIdx + 1} of ${galleryState.photos.length}`;
    // Reset zoom/scroll if the previous photo was pinched.
    const viewport = document.getElementById('pg-viewport');
    if (viewport) {
      viewport.scrollLeft = 0;
      viewport.scrollTop = 0;
    }
  }

  const prevBtn = document.getElementById('pg-prev');
  const nextBtn = document.getElementById('pg-next');
  if (prevBtn) {
    prevBtn.style.visibility = galleryState.currentIdx > 0 ? 'visible' : 'hidden';
  }
  if (nextBtn) {
    nextBtn.style.visibility =
      galleryState.currentIdx < galleryState.photos.length - 1 ? 'visible' : 'hidden';
  }
}

function preloadAdjacent(): void {
  // Preload current ± 1 so the swap on navigate is instant.
  // Skips if out of bounds.
  const idx = galleryState.currentIdx;
  [idx - 1, idx + 1].forEach((i) => {
    if (i >= 0 && i < galleryState.photos.length) {
      const img = new Image();
      img.src = galleryState.photos[i].url;
    }
  });
}

// ─── Rendering ─────────────────────────────────────────

function renderGallery(): void {
  const overlay = document.createElement('div');
  overlay.id = 'photo-gallery-overlay';
  overlay.className = 'photo-gallery-overlay';

  const hasMultiple = galleryState.photos.length > 1;
  const startUrl = galleryState.photos[galleryState.currentIdx].url;

  overlay.innerHTML = `
    <div class="pg-backdrop" aria-hidden="true"></div>
    <div class="pg-header">
      <div class="pg-counter" id="pg-counter">${
        hasMultiple ? `${galleryState.currentIdx + 1} of ${galleryState.photos.length}` : ''
      }</div>
      <button class="pg-close-btn" onclick="closePhotoGallery()" aria-label="Close photo gallery">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
      </button>
    </div>
    <div class="pg-viewport" id="pg-viewport">
      <img id="pg-current-img"
           class="pg-slide img-loading"
           src="${escapeHtml(startUrl)}"
           alt="Home photo ${galleryState.currentIdx + 1} of ${galleryState.photos.length}"
           draggable="false"
           onload="this.classList.add('loaded')"
           onerror="imgFallback(this)">
    </div>
    ${
      hasMultiple
        ? `
      <button class="pg-nav pg-nav-prev"
              id="pg-prev"
              onclick="pgPrev()"
              aria-label="Previous photo"
              style="visibility:${galleryState.currentIdx > 0 ? 'visible' : 'hidden'}">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
      </button>
      <button class="pg-nav pg-nav-next"
              id="pg-next"
              onclick="pgNext()"
              aria-label="Next photo"
              style="visibility:${
                galleryState.currentIdx < galleryState.photos.length - 1 ? 'visible' : 'hidden'
              }">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
      </button>
    `
        : ''
    }
  `;

  document.body.appendChild(overlay);

  // The backdrop closes the gallery when tapped (tapping the
  // photo itself shouldn't close — only the backdrop around it).
  const backdrop = overlay.querySelector('.pg-backdrop');
  backdrop?.addEventListener('click', () => closePhotoGallery());
}

// ─── Touch handling ────────────────────────────────────

function bindTouchHandlers(): void {
  const viewport = document.getElementById('pg-viewport');
  if (!viewport) return;

  let startX = 0;
  let startY = 0;
  let dx = 0;
  let dy = 0;
  let isDragging = false;
  let isMultitouch = false;

  const SWIPE_X_THRESHOLD = 50; // px — horizontal swipe to change photo
  const SWIPE_Y_THRESHOLD = 100; // px — swipe down to close
  const SWIPE_DOMINANCE = 1.2; // one axis must dominate the other

  viewport.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      // Two-finger gestures are pinch-zoom — let the browser
      // handle them natively via touch-action: pinch-zoom below.
      if (e.touches.length > 1) {
        isMultitouch = true;
        isDragging = false;
        return;
      }
      const t = e.touches[0];
      startX = t.clientX;
      startY = t.clientY;
      dx = 0;
      dy = 0;
      isDragging = true;
      isMultitouch = false;
    },
    { passive: true },
  );

  viewport.addEventListener(
    'touchmove',
    (e: TouchEvent) => {
      if (!isDragging || isMultitouch) return;
      if (e.touches.length > 1) {
        // User started with one finger, now pinched — bail out.
        isMultitouch = true;
        isDragging = false;
        return;
      }
      const t = e.touches[0];
      dx = t.clientX - startX;
      dy = t.clientY - startY;
    },
    { passive: true },
  );

  viewport.addEventListener('touchend', () => {
    if (!isDragging) {
      isMultitouch = false;
      return;
    }
    isDragging = false;

    // Swipe down to close — only if vertical dominates and
    // exceeds the close threshold.
    if (dy > SWIPE_Y_THRESHOLD && Math.abs(dy) > Math.abs(dx) * SWIPE_DOMINANCE) {
      closePhotoGallery();
      return;
    }

    // Horizontal swipe → prev/next — only if horizontal dominates
    // and exceeds the swipe threshold.
    if (Math.abs(dx) > SWIPE_X_THRESHOLD && Math.abs(dx) > Math.abs(dy) * SWIPE_DOMINANCE) {
      if (dx < 0) pgNext();
      else pgPrev();
    }
  });

  viewport.addEventListener('touchcancel', () => {
    isDragging = false;
    isMultitouch = false;
  });
}
